import { useState, useEffect } from "react"
import { useImageViewer } from "@/features/accounts/ocr/hooks/use-images-viewer"
import { ImageFile } from "@/features/accounts/ocr/hooks/use-images-viewer-states"
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { OcrImageDeleteModal } from "@/features/accounts/ocr/components/ocr-image-delete-modal";

interface Props {
  imageFiles: ImageFile[]
  selectedImage?: ImageFile
  viewMode: "check" | "preview"
  tradeId?: string
  processId?: string
  onImageSelect: (filename: string) => void
  onImagesLoad?: (images: ImageFile[]) => void
  onProcessedComplete?: (filename: string) => void
}

export const ImageViewer = (props: Props) => {
  const { openHomeModal,setOpenHomeModal } = useModalContext();
  const {
    imageFiles = [],
    selectedImage,
    viewMode = "check",
    tradeId,
    processId,
    onImageSelect = () => {},
    onImagesLoad = () => {},
    onProcessedComplete = () => {},
  } = props

  const {
    imagesList,
    setImages,
    cancelImage,
  } = useImageViewer()

  const {
    selectedAccountGroup,
    selectedAccount
  } = useAccountGroupsContext();

  const [isLoading, setIsLoading] = useState(true)
  // 削除対象ファイル
  const [deleteTargetFilename, setDeleteTargetFilename] = useState<string | undefined>(undefined);
  // 削除対象のファイル名
  const [deleteTargetFileOriginName, setDeleteTargetFileOriginName] = useState<string | undefined>(undefined);


  // 初回表示時に画像リストの取得
  useEffect(() => {
    const fetchImages = async () => {
      setIsLoading(true)
      try {
        if (!selectedAccount) {
          setIsLoading(false)
          return;
        }
        if (viewMode === "check") {
          await setImages(selectedAccountGroup.accountGroupId, selectedAccount.mlinkAccountId, selectedAccount.ocrProcessId, undefined)
        } else {
          await setImages(selectedAccountGroup.accountGroupId, selectedAccount.mlinkAccountId, processId, tradeId)
        }
      } catch (e) {
        // APIエラー時は親に即座に戻る（onProcessedCompleteを呼ぶ）
        console.log("ImageViewer fetchImages error:", e)
          onProcessedComplete("");
        return;
      } finally {
        setIsLoading(false);
      }
    };
    fetchImages();
  }, [])

  // 画像を取り込まないボタンを押したときの処理
  const handleSkipImage = async (filename: string) => {
    try {
      if (!selectedAccount || !selectedImage) {
        return
      }
      await cancelImage(selectedAccountGroup.accountGroupId, selectedAccount.mlinkAccountId, selectedAccount.ocrProcessId, filename);
      // 処理完了コールバック
      onProcessedComplete(filename);
    } catch (e) {
      console.log("ImageViewer handleSkipImage error:", e)
    }
  }


  // 画像リストが更新されたら親コンポーネントに通知・初期選択
  useEffect(() => {
    if (imagesList.length > 0) {
      onImagesLoad(imagesList)
    }
  }, [imagesList])

  if (isLoading) {
    return (
      <div className="w-1/2 bg-background rounded-lg shadow-xl flex items-center justify-center">
        <div className="text-muted-foreground">画像を読み込み中...</div>
      </div>
    )
  }

  // 表示する画像リストをviewModeで切り替え
  const displayImageFiles = viewMode === "check"
    ? imageFiles.filter((file) => !file.isCompleted)
    : imageFiles;

  if (!displayImageFiles || displayImageFiles.length === 0) {
    return <div>画像がありません</div>;
  }

  if (!selectedImage) {
    return <div>画像を準備中...</div>;
  }

  // 画像削除ボタンに対応するID
  const deleteImage = "delete_image"

  /* NOTE: 値は感覚値 */
  const pinchStep = 0.03;
  const wheelStep = 0.003;

  return (
    <>
    <section id="ocrViewer" className="ocr_viewer">
      {/* 画像表示エリア */}
      <div
        id="dspImage"
        // style={{height: "40rem"}}
      >
        <TransformWrapper
          pinch={{ step: pinchStep }}
          wheel={{ smoothStep: wheelStep }}
          minScale={0.5}
        >
          <TransformComponent
          wrapperStyle={{ width: "100%", height: "100%" }}
          >
            <img
              src={selectedImage.url}
              id={selectedImage.filename}
              alt={selectedImage.originFilename}
              className="active"
            />
          </TransformComponent>
        </TransformWrapper>
      </div>

      {/* 画像名リスト */}
      {viewMode === "check" && (
        <ul id="thumbnails">
          {displayImageFiles.map((file) => (
            <li key={file.filename}>
              <BaseRoundButton
                buttonText={file.originFilename}
                className={`thum${selectedImage.filename.toLowerCase() === file.filename.toLowerCase() ? " active" : ""}`}
                onClick={() => onImageSelect(file.filename)}
              />
              <BaseRoundButton
                title="削除"
                className="del"
                onClick={() => {
                  setDeleteTargetFilename(file.filename);
                  setDeleteTargetFileOriginName(file.originFilename);
                  setOpenHomeModal("OcrImageDelete");
                }}
                id={deleteImage}
                loadingNegative={true}
              />
            </li>
          ))}
        </ul>
      )}
    </section>

    {openHomeModal === "OcrImageDelete" && (
      <OcrImageDeleteModal
        ocrImageFilename={deleteTargetFileOriginName}
        onTapCloseModal={() => {
          setOpenHomeModal(undefined);
          setDeleteTargetFilename(undefined);
          setDeleteTargetFileOriginName(undefined);
        }}
        onTapCompleteModal={() => {
          setOpenHomeModal(undefined);
          if (deleteTargetFilename) {
            handleSkipImage(deleteTargetFilename);
            setDeleteTargetFilename(undefined);
          }
        }}
      />
    )}
    </>
  )
}
